import { Button } from "@/components/ui/button";
import { Phone, Headphones, PhoneCall, Menu, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import ellenHeroWebp from "@/assets/ellen-hero.webp";
import ellenHero from "@/assets/ellen-hero.jpg";
import { useState } from "react";

const navLinks = [
  { href: "#portfolio", label: "Portfólio" },
  { href: "#about", label: "Benefícios" },
  { href: "#about-ellen", label: "Sobre Ellen" },
  { href: "#ura-generator", label: "Gerador de URA" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contato" },
];

export const Hero = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className="relative min-h-[90vh] flex flex-col overflow-hidden bg-gradient-hero" role="banner">
      {/* Navigation Bar */}
      <nav className="relative z-20 w-full py-4" aria-label="Navegação principal">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <a href="/" className="text-xl font-bold gradient-text" aria-label="URA Humanizada - Página inicial">
              URA Humanizada
            </a>
            
            {/* Desktop Navigation */}
            <ul className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <ul className="lg:hidden mt-4 py-4 border-t border-border/20 space-y-3 animate-fadeIn">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="block py-2 text-base font-medium text-foreground/80 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </nav>

      <div className="flex-1 flex items-center relative">
      {/* Phone pattern background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20">
          <Phone className="w-32 h-32 text-primary animate-float" />
        </div>
        <div className="absolute bottom-32 right-32" style={{ animationDelay: '1s' }}>
          <Headphones className="w-40 h-40 text-accent animate-float" />
        </div>
        <div className="absolute top-1/2 left-1/3" style={{ animationDelay: '2s' }}>
          <PhoneCall className="w-24 h-24 text-primary animate-float" />
        </div>
      </div>

      {/* Animated background waves */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>
      
      <div className="container mx-auto px-4 py-12 lg:py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-6 lg:space-y-8 animate-slideInLeft text-center lg:text-left">
            <div className="space-y-4 lg:space-y-6 flex flex-col items-center lg:items-start">
              <div className="inline-flex items-center gap-2 lg:gap-3 px-4 lg:px-5 py-2 lg:py-3 rounded-full bg-primary/10 backdrop-blur-md border-2 border-primary/30 shadow-lg">
                <Phone className="w-4 h-4 lg:w-5 lg:h-5 text-primary animate-pulse" />
                <p className="text-primary font-bold text-sm lg:text-base tracking-wide">Atendimento Humanizado</p>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-foreground leading-tight">
                Gravação de <span className="gradient-text inline-block animate-scaleIn">URA Humanizada</span>
              </h1>
            </div>
            
            <p className="text-base sm:text-lg lg:text-2xl text-muted-foreground max-w-xl leading-relaxed font-medium mx-auto lg:mx-0">
              Transforme o atendimento telefônico da sua empresa com gravações humanizadas e profissionais
            </p>

            {/* Mobile Image - Show only on mobile */}
            <div className="lg:hidden flex justify-center mt-6">
              <picture>
                <source srcSet={ellenHeroWebp} type="image/webp" />
                <img 
                  src={ellenHero} 
                  alt="Ellen, locutora profissional de URA humanizada, especialista em atendimento telefônico" 
                  className="w-64 h-auto rounded-3xl shadow-2xl"
                  width="448"
                  height="671"
                  {...({ fetchpriority: "high" } as any)}
                />
              </picture>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3 lg:gap-4 pt-2 lg:pt-4">
              <Card className="p-3 lg:p-4 bg-gradient-phone border-2 border-primary/20 hover-lift" role="article" aria-label="Atendimento disponível 24 horas por dia, 7 dias por semana">
                <Phone className="w-6 h-6 lg:w-8 lg:h-8 text-primary mb-1 lg:mb-2" aria-hidden="true" />
                <p className="font-semibold text-xs lg:text-sm">Atendimento 24/7</p>
              </Card>
              <Card className="p-3 lg:p-4 bg-gradient-phone border-2 border-accent/20 hover-lift" role="article" aria-label="Gravação com voz clara e natural">
                <Headphones className="w-6 h-6 lg:w-8 lg:h-8 text-accent mb-1 lg:mb-2" aria-hidden="true" />
                <p className="font-semibold text-xs lg:text-sm">Voz Clara e Natural</p>
              </Card>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 pt-2 lg:pt-4">
              <Button 
                size="lg" 
                variant="highlight"
                className="transition-all duration-300 text-base lg:text-lg px-6 py-6 lg:px-10 lg:py-7 rounded-xl font-bold w-full sm:w-auto"
                asChild
              >
                <a 
                  href="https://api.whatsapp.com/send?phone=5511910456805&text=Olá,%20gostaria%20de%20um%20orçamento" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Solicitar orçamento via WhatsApp"
                >
                  <Phone className="mr-2 lg:mr-3 h-5 w-5 lg:h-6 lg:w-6" aria-hidden="true" />
                  Solicitar Orçamento
                </a>
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-primary hover:bg-primary/5 transition-all duration-300 text-base lg:text-lg px-6 py-6 lg:px-10 lg:py-7 rounded-xl font-bold w-full sm:w-auto"
                onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
                aria-label="Navegar para a seção de exemplos de áudio"
              >
                Ouvir Exemplos
              </Button>
            </div>
          </div>
          
          <div className="relative hidden lg:flex lg:h-[700px] items-center justify-center animate-slideInRight mt-8 lg:mt-0">
            <div className="relative w-full max-w-[280px] sm:max-w-sm lg:max-w-lg mx-auto">
              {/* Phone device mockup */}
              <div className="relative">
                {/* Glowing rings */}
                <div className="absolute inset-0 hidden lg:block">
                  <div className="absolute inset-0 border-2 border-primary/20 rounded-[3rem]" />
                  <div className="absolute inset-8 border-2 border-accent/20 rounded-[2.5rem]" />
                </div>
                
                {/* Main phone */}
                <div className="relative bg-gradient-to-br from-secondary to-secondary/80 rounded-[2rem] lg:rounded-[3rem] p-2 lg:p-4 shadow-phone border-4 lg:border-8 border-background transition-transform duration-500">
                  {/* Screen */}
                  <div 
                    className="rounded-[1.5rem] lg:rounded-[2.5rem] p-4 lg:p-8 min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] flex flex-col justify-end relative overflow-hidden"
                    role="img"
                    aria-label="Dispositivo móvel mostrando Ellen, locutora profissional de URA humanizada"
                  >
                    <picture className="absolute inset-0">
                      <source srcSet={ellenHeroWebp} type="image/webp" />
                      <img 
                        src={ellenHero} 
                        alt="" 
                        className="absolute inset-0 w-full h-full object-cover rounded-[1.5rem] lg:rounded-[2.5rem]"
                        width="448"
                        height="671"
                        {...({ fetchpriority: "high" } as any)}
                        aria-hidden="true"
                      />
                    </picture>
                    {/* Overlay gradient for better text visibility */}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-primary/15 to-transparent" />
                    
                    {/* Call interface - moved to bottom */}
                    <div className="text-center space-y-4 lg:space-y-8 relative z-10 pb-4 lg:pb-8">
                      <div className="flex gap-3 lg:gap-4 justify-center">
                        <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-full bg-accent shadow-lg flex items-center justify-center">
                          <PhoneCall className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
                        </div>
                        <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-full bg-primary shadow-lg flex items-center justify-center">
                          <Phone className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
                        </div>
                      </div>

                      <div className="space-y-1 lg:space-y-2">
                        <p className="text-white text-lg lg:text-2xl font-bold drop-shadow-lg">Ellen - Locutora</p>
                        <p className="text-white/90 text-sm lg:text-lg drop-shadow-md">URA Humanizada</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Home button */}
                  <div className="w-12 lg:w-16 h-1 lg:h-1.5 bg-white/20 rounded-full mx-auto mt-2 lg:mt-4" />
                </div>

                {/* Floating elements - hidden on mobile for cleaner look */}
                <div className="hidden sm:flex absolute -top-6 lg:-top-8 -left-6 lg:-left-8 w-16 h-16 lg:w-20 lg:h-20 bg-primary/20 rounded-full animate-float backdrop-blur-sm items-center justify-center">
                  <Phone className="w-8 h-8 lg:w-10 lg:h-10 text-primary" />
                </div>
                <div className="hidden sm:flex absolute -bottom-6 lg:-bottom-8 -right-6 lg:-right-8 w-20 h-20 lg:w-24 lg:h-24 bg-accent/20 rounded-full animate-float backdrop-blur-sm items-center justify-center" style={{ animationDelay: '1s' }}>
                  <Headphones className="w-10 h-10 lg:w-12 lg:h-12 text-accent" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </header>
  );
};
